// Lưu / nạp chú thích VolView ở RIS khi VolView nằm trong trang DicomView của RIS (iframe). VolView không gọi thẳng RIS
// (khác nguồn, không có phiên): nhờ trang cha — trang đó có giấy phép đọc (srToken) / ghi (ghiToken, chỉ người có quyền
// Lập kết quả của ca). Không nằm trong RIS, hoặc RIS không trả lời, thì nút Lưu vẫn tải tệp phiên về máy như VolView gốc.
// Giao thức (postMessage):
//   VolView -> RIS  {type:'volview-chu-thich-hoi'}                       lúc mở
//   RIS -> VolView  {type:'ris-chu-thich', coTheLuu, zip?, loi?}           zip = gói đã lưu (nếu có)
//   VolView -> RIS  {type:'volview-chu-thich-luu', id, zip}                bấm Lưu
//   RIS -> VolView  {type:'ris-chu-thich-da-luu', id, ok, message}
// Đặc tả: RisSolution docs/thiet-ke/dac-ta/2026-09-29-luu-chu-thich-volview.md
import JSZip from 'jszip';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { Manifest, StateFile } from '@/src/io/state-file/schema';
import type { FileEntry } from '@/src/io/types';
import { extractFilesFromZip } from '@/src/io/zip';
import { normalize } from '@/src/utils/path';
import { makeSegmentGroupArchivePath } from '@/src/io/state-file/segmentGroupArchivePath';
import {
  ANNOTATION_KEYS,
  AnnotationKey,
  RIS_MANIFEST,
  RisManifest,
  SerializedSegmentGroup,
  SerializedToolGroup,
  emptyRisManifest,
  mergeRisManifests,
  omitImages,
  parseRisManifest,
  pickForImages,
  volumeKeyOf,
} from '@/src/io/state-file/risAnnotations';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useSegmentGroupStore } from '@/src/store/segmentGroups';
import { useMessageStore } from '@/src/store/messages';
import { useRulerStore } from '@/src/store/tools/rulers';
import { useRectangleStore } from '@/src/store/tools/rectangles';
import { usePolygonStore } from '@/src/store/tools/polygons';

const TRAN_GOI = 50 * 1024 * 1024; // khớp VolViewChuThich.TranGoi phía RIS
const CHO_TRA_LOI_MS = 60_000;

type KhoCongCu = {
  serialize?: (state: StateFile) => void;
  findLabel: (name?: string) => [string, unknown] | undefined;
  addLabel: (label?: Record<string, unknown>) => string;
  addTool: (tool: Record<string, unknown>) => string;
};

const khoCongCu: Record<AnnotationKey, () => KhoCongCu> = {
  rulers: () => useRulerStore() as unknown as KhoCongCu,
  rectangles: () => useRectangleStore() as unknown as KhoCongCu,
  polygons: () => usePolygonStore() as unknown as KhoCongCu,
};

type KetQuaLuu = { ok: boolean; message: string };

// Nguồn trang cha để postMessage đúng người nhận; trình duyệt không cho biết thì đành '*' (gói chỉ có chú thích, không ảnh).
function nguonCha() {
  const theoTrinhDuyet = window.location.ancestorOrigins?.[0];
  if (theoTrinhDuyet) return theoTrinhDuyet;
  try {
    return document.referrer ? new URL(document.referrer).origin : '*';
  } catch {
    return '*';
  }
}

const loiThanh = (err: unknown) =>
  err instanceof Error ? err : new Error(String(err));

export const useRisAnnotationStore = defineStore('risAnnotations', () => {
  const coTheLuu = ref(false);
  const isSaving = ref(false);

  // Chú thích đã lưu mà khối ảnh của nó chưa có trong phiên này: chờ khối xuất hiện, lưu lại thì giữ nguyên.
  // Tệp lớp phân vùng chỉ bung khi cần (layTep): bung cả gói trước khi gắn thước đo là bắt bác sĩ chờ vô ích.
  let cho: {
    manifest: RisManifest;
    layTep: () => Promise<FileEntry[]>;
  } | null = null;
  let hangApDung: Promise<void> = Promise.resolve();
  let luot = 0;
  const choTraLoi = new Map<number, (kq: KetQuaLuu) => void>();

  function guiCha(tin: Record<string, unknown>, chuyen: Transferable[] = []) {
    window.parent.postMessage(tin, nguonCha(), chuyen);
  }

  async function apDungNgay() {
    if (!cho) return;
    const theoKhoa = new Map<string, string>();
    Object.entries(useDICOMStore().volumeInfo).forEach(([id, info]) =>
      theoKhoa.set(volumeKeyOf(info), id)
    );
    const map: Record<string, string> = {};
    Object.entries(cho.manifest.volumes).forEach(([cu, khoa]) => {
      const moi = theoKhoa.get(khoa);
      if (moi) map[cu] = moi;
    });
    const daGhep = new Set(Object.keys(map));
    if (!daGhep.size) return;

    const phan = pickForImages(cho.manifest, daGhep);
    const { layTep } = cho;

    // Công cụ đo trước (tức thì); lớp phân vùng sau (đọc tệp qua worker, chậm). Mỗi phần chỉ rời hàng chờ khi đã vào kho:
    // rời trước mà gắn hỏng thì lần Lưu sau xoá mất nó.
    // Công cụ đo: thêm thẳng vào kho; nhãn ghép theo tên để mở nhiều lần không nhân đôi nhãn.
    ANNOTATION_KEYS.forEach((key) => {
      const nhom = phan.tools[key];
      if (!nhom?.tools.length) return;
      const kho = khoCongCu[key]();
      // Mã công cụ cũ đi theo `con` nhưng addTool luôn cấp mã mới.
      nhom.tools.forEach(({ imageID, label, ...con }) => {
        const nhan = label ? nhom.labels[label] : undefined;
        const maNhan = nhan
          ? (kho.findLabel(nhan.labelName)?.[0] ?? kho.addLabel(nhan))
          : '';
        kho.addTool({ ...con, imageID: map[imageID], label: maNhan });
      });
    });
    cho = {
      layTep,
      manifest: {
        ...cho.manifest,
        tools: omitImages(cho.manifest, daGhep).tools,
      },
    };

    // Lớp phân vùng: đúng hàm khôi phục phiên của VolView (đọc tệp trong gói, gắn vào ảnh theo map).
    if (phan.segmentGroups.length)
      await useSegmentGroupStore().deserialize(
        { segmentGroups: phan.segmentGroups } as unknown as Manifest,
        await layTep(),
        map
      );
    if (cho) cho = { ...cho, manifest: omitImages(cho.manifest, daGhep) };
  }

  function apDung() {
    hangApDung = hangApDung.then(apDungNgay).catch((err) => {
      useMessageStore().addError('Không gắn được chú thích đã lưu vào ảnh', {
        error: loiThanh(err),
      });
    });
  }

  async function napGoi(goi: ArrayBuffer) {
    try {
      const zip = await JSZip.loadAsync(goi);
      const text = await zip.file(RIS_MANIFEST)?.async('string');
      if (text == null) throw new Error('Gói chú thích thiếu manifest.json.');
      const manifest = parseRisManifest(text);
      let tep: Promise<FileEntry[]> | null = null;
      const layTep = () => {
        tep ??= extractFilesFromZip(new File([goi], 'chu-thich.zip')).then(
          (ds) => ds.filter((f) => f.file.name !== RIS_MANIFEST)
        );
        return tep;
      };
      cho = { manifest, layTep };
      apDung();
    } catch (err) {
      useMessageStore().addError('Không đọc được chú thích đã lưu', {
        error: loiThanh(err),
      });
    }
  }

  function nhanTin(e: MessageEvent) {
    if (e.source !== window.parent) return;
    const d = e.data;
    if (!d || typeof d.type !== 'string') return;
    if (d.type === 'ris-chu-thich') {
      coTheLuu.value = d.coTheLuu === true;
      if (d.loi)
        useMessageStore().addError('Không tải được chú thích đã lưu', {
          details: String(d.loi),
        });
      if (d.zip instanceof ArrayBuffer) napGoi(d.zip);
    } else if (d.type === 'ris-chu-thich-da-luu') {
      const xong = choTraLoi.get(d.id);
      if (!xong) return;
      choTraLoi.delete(d.id);
      xong({ ok: d.ok === true, message: String(d.message ?? '') });
    }
  }

  function ketNoi() {
    if (window.parent === window) return;
    window.addEventListener('message', nhanTin);
    // Khối ảnh nạp dần (chọn khối khác trong tab Ca chụp): khối nào xuất hiện thì gắn chú thích của khối đó.
    watch(
      () => Object.keys(useDICOMStore().volumeInfo).length,
      () => {
        if (cho) apDung();
      }
    );
    guiCha({ type: 'volview-chu-thich-hoi' });
  }

  async function dongGoi(): Promise<ArrayBuffer> {
    const { volumeInfo } = useDICOMStore();
    const laDicom = (id?: string) => !!id && id in volumeInfo;
    const zip = new JSZip();
    const state = {
      zip,
      manifest: { tools: {}, segmentGroups: [] },
    } as unknown as StateFile;
    ANNOTATION_KEYS.forEach((key) => khoCongCu[key]().serialize?.(state));
    // Lớp phân vùng ghi dạng seg.nrrd nén (itk-wasm, trong worker): vti ghi thô ~1 byte/điểm ảnh, mở lại phải bung rồi
    // phân tích cả trăm MB. Đổi tạm định dạng rồi trả lại — đó là lựa chọn của người dùng cho nút tải phân vùng về máy.
    const segStore = useSegmentGroupStore();
    const dinhDangCu = segStore.saveFormat;
    segStore.saveFormat = 'seg.nrrd';
    try {
      await segStore.serialize(state);
    } finally {
      segStore.saveFormat = dinhDangCu;
    }

    const m = emptyRisManifest();
    const dung = new Set<string>();
    const tools = state.manifest.tools as unknown as Partial<
      Record<AnnotationKey, SerializedToolGroup>
    >;
    ANNOTATION_KEYS.forEach((key) => {
      const nhom = tools[key];
      if (!nhom) return;
      const giu = nhom.tools.filter((t) => laDicom(t.imageID));
      giu.forEach((t) => dung.add(t.imageID));
      m.tools[key] = { tools: giu, labels: nhom.labels ?? {} };
    });
    (
      (state.manifest.segmentGroups ??
        []) as unknown as SerializedSegmentGroup[]
    ).forEach((g) => {
      if (laDicom(g.metadata.parentImage)) {
        m.segmentGroups.push(g);
        dung.add(g.metadata.parentImage);
      } else if (g.path) zip.remove(g.path);
    });
    dung.forEach((id) => {
      m.volumes[id] = volumeKeyOf(volumeInfo[id]);
    });

    let goi = m;
    const conCho = cho;
    if (conCho) {
      const daDung = new Set<string>();
      zip.forEach((p) => daDung.add(normalize(p).toLowerCase()));
      const tepCho = conCho.manifest.segmentGroups.length
        ? await conCho.layTep()
        : [];
      const nhomCho = conCho.manifest.segmentGroups.flatMap((g) => {
        const tep = g.path
          ? tepCho.find((f) => f.archivePath === normalize(g.path!))
          : undefined;
        if (!tep) return [];
        const duoi = /\.seg\.nrrd$/i.test(g.path!)
          ? 'seg.nrrd'
          : g.path!.split('.').pop() || 'vti';
        const path = makeSegmentGroupArchivePath(g.metadata.name, duoi, daDung);
        zip.file(path, tep.file);
        return [{ ...g, path }];
      });
      goi = mergeRisManifests(m, {
        ...conCho.manifest,
        segmentGroups: nhomCho,
      });
    }
    zip.file(RIS_MANIFEST, JSON.stringify(goi));
    return zip.generateAsync({
      type: 'arraybuffer',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });
  }

  async function luu() {
    if (isSaving.value) return;
    isSaving.value = true;
    const messageStore = useMessageStore();
    try {
      await hangApDung; // khôi phục chưa xong mà lưu là thiếu phần đang gắn
      const goi = await dongGoi();
      if (goi.byteLength > TRAN_GOI)
        throw new Error(
          `Gói chú thích ${(goi.byteLength / 1048576).toFixed(1)} MB, quá giới hạn 50 MB — bớt lớp phân vùng rồi lưu lại.`
        );
      luot += 1;
      const id = luot;
      const kq = await new Promise<KetQuaLuu>((resolve) => {
        const hen = setTimeout(() => {
          choTraLoi.delete(id);
          resolve({
            ok: false,
            message: 'RIS không trả lời — kiểm tra kết nối rồi lưu lại.',
          });
        }, CHO_TRA_LOI_MS);
        choTraLoi.set(id, (x) => {
          clearTimeout(hen);
          resolve(x);
        });
        guiCha({ type: 'volview-chu-thich-luu', id, zip: goi }, [goi]);
      });
      if (kq.ok)
        messageStore.addSuccess(kq.message || 'Đã lưu chú thích vào RIS.');
      else
        messageStore.addError('Chưa lưu được chú thích', {
          details: kq.message,
        });
    } catch (err) {
      messageStore.addError('Chưa lưu được chú thích', {
        error: loiThanh(err),
      });
    } finally {
      isSaving.value = false;
    }
  }

  return { coTheLuu, isSaving, ketNoi, luu };
});
